import { createSlice } from '@reduxjs/toolkit'
import PipelineService, { LIST_PAGE_SIZE } from '../services/pipelineService'

const initialState = {
    // The rows the list shows: the page on screen, or one event type's whole
    // list (which the page divides into pages itself).
    pipelines: [],
    pipeline: {
        pipeline_name: '',
        event_type: '',
        comms: [],
        schedule: 0,
        comment: '',
        niceness: false,
        absolute_time: false,
    },
    pipelineHistory: [],
    deletedPipeline: {},
    error: '',
    eventTypes: [],
    selectedEventType: '',
    // Paging of the status list and its search, read a page at a time.
    // `pages[n]` holds page n's rows once loaded; `cursors[n]` is the cursor
    // that loads page n (page 0 needs none; null after the last page). Pages
    // already loaded are kept, so Previous needs no request; a new list
    // (status, search, event type or page size) starts over at page 0.
    pages: [],
    cursors: [null],
    pageIndex: 0,
    pageSize: LIST_PAGE_SIZE,
    // A page after the one shown exists (loaded, or a cursor to load it).
    hasNext: false,
    // The list request whose answer the page shows (getPipelines or the first
    // page of listPipelines). Answers to older requests are dropped: a
    // slow unfiltered page (about 5 s on dev) landing after a fast filtered
    // one replaced it under the event type's label.
    listRequestId: null,
    // True from a new list request until its answer: the old rows are hidden
    // (they stayed under the new label, and Delete nearly hit the wrong row).
    listLoading: false,
    // A later page's request (Next, or reloading the page shown after a
    // delete), and the list request it belongs to; its rows are kept only
    // while that list is still the one shown. Rows are hidden meanwhile.
    pageRequestId: null,
    pageBaseId: null,
    pageLoading: false,
    // GET /comms/v2/pipeline/create/enums: receiver types and each existing
    // event type's receiver. null until loaded; `enumsFailed` when it could
    // not be (the wizard then offers its own list and locks nothing).
    enums: null,
    enumsFailed: false,
}

/** True for a request of a page after the first: one that carries its index. */
const isPageLoad = (action) => (action.meta.arg?.page ?? 0) > 0

/** Whether a page after the one shown exists (loaded, or a cursor to load it). */
const refreshHasNext = (state) => {
    state.hasNext =
        state.pageIndex < state.pages.length - 1 ||
        !!state.cursors[state.pageIndex + 1]
}

/** Show loaded page `n`. */
const showPage = (state, n) => {
    state.pageIndex = n
    state.pipelines = state.pages[n] ?? []
    refreshHasNext(state)
}

/** A new list request: it becomes the one the page shows, from page 0. */
const startList = (state, action) => {
    state.listRequestId = action.meta.requestId
    state.listLoading = true
    state.pipelines = []
    state.pages = []
    state.cursors = [null]
    state.pageIndex = 0
    state.hasNext = false
    state.pageRequestId = null
    state.pageBaseId = null
    state.pageLoading = false
}

/** The shown list request failed: stop loading and keep the reason; older requests are ignored. */
const listFailed = (state, action) => {
    if (action.meta.requestId !== state.listRequestId) return
    state.listLoading = false
    state.error = action.payload?.error ?? 'Failed to load pipelines'
}

/**
 * Keep page `n`'s answer: it replaces page n and drops the pages after it
 * (their cursors may have moved), and is shown. An empty page with no
 * cursor after it (the last page read exactly to its end, or emptied by a
 * delete) is not kept: the page before it is shown, with no Next.
 */
const keepPage = (state, n, rows, nextCursor) => {
    if (n > 0 && rows.length === 0 && !nextCursor) {
        state.pages = state.pages.slice(0, n)
        state.cursors = [...state.cursors.slice(0, n), null]
        showPage(state, n - 1)
        return
    }
    state.pages = [...state.pages.slice(0, n), rows]
    state.cursors = [...state.cursors.slice(0, n + 1), nextCursor]
    showPage(state, n)
}

const pipelineSlice = createSlice({
    name: 'pipeline',
    initialState,
    reducers: {
        putPipelineData: (state, action) => {
            state.pipeline = { ...state.pipeline, ...action.payload }
        },
        resetPipelineData: (state) => {
            state.pipeline = initialState.pipeline
        },
        putSelectedEventType: (state, action) => {
            state.selectedEventType = action.payload.selectedEventType
        },
        /** Show a page already loaded (Previous, or Next onto a cached page): no request. */
        showPipelinePage: (state, action) => {
            const n = action.payload
            if (!state.pages[n] || state.pageLoading) return
            showPage(state, n)
        },
        /** Rows per page; the page then loads its list again from page 0. */
        setPipelinePageSize: (state, action) => {
            state.pageSize = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(PipelineService.getPipelines.pending, startList)
            .addCase(
                PipelineService.getPipelines.fulfilled,
                (state, action) => {
                    if (action.meta.requestId !== state.listRequestId) return
                    // One event type's whole list: the page divides it.
                    state.pipelines = action.payload.data
                    state.listLoading = false
                }
            )
            .addCase(PipelineService.getPipelines.rejected, listFailed)
            .addCase(PipelineService.listPipelines.pending, (state, action) => {
                if (!isPageLoad(action)) {
                    startList(state, action)
                    state.pageSize = action.meta.arg?.pageSize ?? state.pageSize
                    return
                }
                state.pageRequestId = action.meta.requestId
                state.pageBaseId = state.listRequestId
                state.pageLoading = true
            })
            .addCase(
                PipelineService.listPipelines.fulfilled,
                (state, action) => {
                    const page = action.payload.data ?? {}
                    const rows = page.pipelines ?? []
                    const nextCursor = page.last_evaluated_key ?? null
                    if (isPageLoad(action)) {
                        const current =
                            action.meta.requestId === state.pageRequestId &&
                            state.pageBaseId === state.listRequestId
                        if (!current) return
                        state.pageLoading = false
                        state.pageRequestId = null
                        keepPage(state, action.meta.arg.page, rows, nextCursor)
                        return
                    }
                    if (action.meta.requestId !== state.listRequestId) return
                    state.listLoading = false
                    keepPage(state, 0, rows, nextCursor)
                }
            )
            .addCase(
                PipelineService.listPipelines.rejected,
                (state, action) => {
                    if (isPageLoad(action)) {
                        // The page shown stays; only the current request
                        // may stop the loading.
                        if (action.meta.requestId !== state.pageRequestId)
                            return
                        state.pageLoading = false
                        state.pageRequestId = null
                        state.error =
                            action.payload?.error ?? 'Failed to load pipelines'
                        return
                    }
                    listFailed(state, action)
                }
            )
            .addCase(
                PipelineService.getPipelineEnums.fulfilled,
                (state, action) => {
                    state.enums = action.payload.data?.enums ?? null
                    state.enumsFailed = !state.enums
                }
            )
            .addCase(PipelineService.getPipelineEnums.rejected, (state) => {
                state.enumsFailed = true
            })
            .addCase(PipelineService.createPipeline.fulfilled, (state) => {
                state.pipeline = initialState.pipeline
            })
            .addCase(PipelineService.updatePipeline.fulfilled, (state) => {
                // Reset after edits too, so stale data never bleeds into the
                // next New Pipeline flow.
                state.pipeline = initialState.pipeline
            })
            .addCase(PipelineService.getPipelineHistory.pending, (state) => {
                // Another pipeline's versions must not show while these load.
                state.pipelineHistory = []
            })
            .addCase(
                PipelineService.getPipelineHistory.fulfilled,
                (state, action) => {
                    state.pipelineHistory = action.payload.data
                }
            )
            .addCase(
                PipelineService.getPipelineHistory.rejected,
                (state, action) => {
                    state.error =
                        action.payload?.error ?? 'Failed to load history'
                }
            )
            .addCase(
                PipelineService.deletePipeline.fulfilled,
                (state, action) => {
                    state.deletedPipeline = action.payload.data
                }
            )
            .addCase(
                PipelineService.deletePipeline.rejected,
                (state, action) => {
                    state.error = action.payload?.error ?? 'Failed to delete'
                }
            )
            .addCase(
                PipelineService.getEventTypes.fulfilled,
                (state, action) => {
                    state.eventTypes = action.payload.data
                }
            )
            .addCase(
                PipelineService.getEventTypes.rejected,
                (state, action) => {
                    state.error =
                        action.payload?.error ?? 'Failed to load event types'
                }
            )
    },
})

export const {
    putPipelineData,
    resetPipelineData,
    putSelectedEventType,
    showPipelinePage,
    setPipelinePageSize,
} = pipelineSlice.actions

export default pipelineSlice.reducer
